import { useCallback, useEffect, useRef, useState } from "react";

// Hover-open state for Radix Popover. Mouse-leave is deferred by `closeDelayMs`
// so the cursor can move from trigger to popover content without re-closing.
//
// Also supplies a scroll-safe close-focus-restore. Radix's own close-time
// restore (`context.triggerRef.current?.focus()` in @radix-ui/react-popover,
// both PopoverContentModal and PopoverContentNonModal) calls `.focus()` with
// no `preventScroll`. Every hover-opened Popover in this app has its trigger
// inside the `position: sticky` header, and the browser's native
// scroll-into-view-on-focus targets a sticky element's un-stuck layout
// position (near the top of the document) rather than its visually-stuck
// one, snapping the whole page up whenever the popover closes (e.g. on
// hover-out). `onCloseAutoFocus` redoes the same restore with
// `preventScroll: true` — gated the same way Radix gates its own (only
// restore if focus fell back to `<body>`, i.e. nothing else already claimed
// it by the user clicking/tabbing elsewhere), so outside interactions still
// win. Wire `ref={triggerRef}` onto the trigger element and
// `onCloseAutoFocus={onCloseAutoFocus}` onto `PopoverContent`. SFCC-11937.
export const useHoverPopover = <T extends HTMLElement>(closeDelayMs = 150) => {
  const [open, setOpen] = useState<boolean>(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<T>(null);

  const handleOpen = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    closeTimer.current = setTimeout(() => setOpen(false), closeDelayMs);
  }, [closeDelayMs]);

  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  const onCloseAutoFocus = useCallback((event: Event) => {
    if (document.activeElement === document.body) {
      triggerRef.current?.focus({ preventScroll: true });
    }
    event.preventDefault();
  }, []);

  return {
    open,
    setOpen,
    handleOpen,
    handleClose,
    triggerRef,
    onCloseAutoFocus,
  };
};
